import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Header, Input } from 'semantic-ui-react';
import { Flex } from '../../Layout/helpers';
import { useTranslation } from 'react-i18next';
import Button from '../../semantic/Button';
import { useFormikContext } from 'formik';

type LabelItem = { id: number; value: string };

export default function ProviderLabels() {
  const { t } = useTranslation();
  const formik = useFormikContext<{ labels: string[] }>();
  const [labelItems, setLabelItems] = useState<LabelItem[]>([]);

  useEffect(() => {
    const currentFormikLabels = formik.values.labels || [];
    const currentInternalLabels = labelItems.map((item) => item.value);

    if (
      JSON.stringify(currentFormikLabels) !==
      JSON.stringify(currentInternalLabels)
    ) {
      setLabelItems(
        currentFormikLabels.map((value, idx) => ({
          id: idx,
          value,
        }))
      );
    }
  }, [formik.values.labels]);

  useEffect(() => {
    const newFormikLabels = labelItems.map((item) => item.value);
    if (
      JSON.stringify(formik.values.labels || []) !==
      JSON.stringify(newFormikLabels)
    ) {
      formik.setFieldValue('labels', newFormikLabels);
    }
  }, [labelItems, formik.setFieldValue]);

  const handleAddRow = () => {
    setLabelItems((prev) => [
      ...prev,
      { id: prev.length > 0 ? prev[prev.length - 1].id + 1 : 0, value: '' },
    ]);
  };

  const handleRemoveRow = (indexToRemove: number) => {
    setLabelItems((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleUpdateValue = (indexToUpdate: number, newValue: string) => {
    setLabelItems((prev) =>
      prev.map((item, idx) =>
        idx === indexToUpdate ? { ...item, value: newValue } : item
      )
    );
  };

  return (
    <Fragment>
      <Header
        icon="tag"
        content={t('editProvider.labels', 'Labels')}
        as="h4"
        dividing
      />
      <Flex direction="column">
        <Button icon="plus" basic onClick={handleAddRow} />
        {labelItems.map((item) => (
          <LabelRow
            key={item.id}
            id={item.id}
            value={item.value}
            onValueChange={handleUpdateValue}
            onRemove={handleRemoveRow}
          />
        ))}
      </Flex>
    </Fragment>
  );
}

type LabelRowProps = {
  id: number;
  value: string;
  onValueChange: (id: number, value: string) => void;
  onRemove: (id: number) => void;
};

function LabelRow({ id, value, onValueChange, onRemove }: LabelRowProps) {
  const { t } = useTranslation();
  return (
    <Flex>
      <Input
        focus
        value={value}
        onChange={(e) => onValueChange(id, e.target.value)}
        placeholder={t('editProvider.label', 'Enter a label...')}
      />
      <Button icon="trash" basic compact onClick={() => onRemove(id)} />
    </Flex>
  );
}
